import React, { useState, useEffect, useCallback } from "react";
import { Grid, Typography } from "@mui/material";
import PageContainer from "../layouts/PageContainer";
import AppCircularProgress from "../components/common/AppCircularProgress";
import DocumentHeaderCard from "../components/documentHeaders/DocumentHeaderCard";
import ViewDocumentHeaderModal from "../components/documentHeaders/ViewDocumentHeaderModal";
import UpdateDocumentHeaderModal from "../components/documentHeaders/UpdateDocumentHeaderModal";
import {
  getDocumentHeaders,
  resetDocumentHeaderServerStatus,
} from "../redux/actions/documentHeaderActions";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import PageError from "../components/common/PageError";
import { DocumentHeaderResponseDto } from "../shared/dtos/document/DocumentHeaderResponseDto";

const DocumentHeadersPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items: documentHeaders,
    loadingFetchAll: loadingDocumentHeaders,
    errorFetchAll: errorLoadingDocumentHeaders,
    serverStatus,
    loadingUpdate: loadingDocumentHeaderUpdate,
    errorUpdate: errorUpdatingDocumentHeader,
  } = useAppSelector((state) => state.documentHeader);

  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [selectedHeader, setSelectedHeader] =
    useState<DocumentHeaderResponseDto | null>(null);

  const canUpdate = hasPermission("document_header:update");

  const fetchHeaders = useCallback(() => {
    dispatch(getDocumentHeaders());
  }, [dispatch]);

  useEffect(() => {
    fetchHeaders();
  }, [fetchHeaders]);

  const handleOpenView = (header: DocumentHeaderResponseDto): void => {
    setSelectedHeader(header);
    setIsViewModalOpen(true);
  };

  const handleOpenUpdate = (header: DocumentHeaderResponseDto): void => {
    setSelectedHeader(header);
    setIsUpdateModalOpen(true);
  };

  useEffect(() => {
    if (serverStatus?.operation === "update" && serverStatus?.status === 200) {
      setIsUpdateModalOpen(false);
      setSelectedHeader(null);
      dispatch(resetDocumentHeaderServerStatus());
    }
  }, [serverStatus, dispatch]);

  const renderContent = (): React.JSX.Element => {
    if (loadingDocumentHeaders) return <AppCircularProgress />;
    if (errorLoadingDocumentHeaders)
      return (
        <PageError error={errorLoadingDocumentHeaders} onRetry={fetchHeaders} />
      );
    if (!documentHeaders || documentHeaders.length === 0)
      return (
        <Typography sx={{ textAlign: "center" }}>
          No document templates found.
        </Typography>
      );

    return (
      <Grid container spacing={3}>
        {documentHeaders.map((header) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={header.id}>
            <DocumentHeaderCard
              documentHeader={header}
              onView={() => handleOpenView(header)}
              onEdit={canUpdate ? () => handleOpenUpdate(header) : undefined}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <PageContainer
      title="Document Templates"
      subtitle="Manage headers, footers, and company identities for printed documents."
    >
      {renderContent()}

      {isViewModalOpen && (
        <ViewDocumentHeaderModal
          open={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          documentHeader={selectedHeader}
        />
      )}

      {isUpdateModalOpen && selectedHeader && (
        <UpdateDocumentHeaderModal
          open={isUpdateModalOpen}
          onClose={() => setIsUpdateModalOpen(false)}
          documentHeader={selectedHeader}
          loading={loadingDocumentHeaderUpdate}
          error={errorUpdatingDocumentHeader}
        />
      )}
    </PageContainer>
  );
};

export default DocumentHeadersPage;
